import {
  Fragment,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { getCircleColorBiologicals } from "../../../../shared/layout/Plots/utils";
import { getProductUnit } from "../../../create-protocol/utils";
import ProductCard from "../ProductCard";
import PlotSummary from "../PlotSummary";
import { mergeStripPlotsIntoGroup, type useNewTrialState } from "../../legacy";
import { buildNutrientGroups, groupLetter } from "../../utils/nutrientPlots";
import { resolveSeedFlow } from "../../utils/seedFlow";
import {
  buildBiologicalAssignmentsSnapshot,
  buildBiologicalReset,
  buildBiologicalTreatmentAssignmentsPayload,
} from "../../utils/biologicalAssignments";
import { fetchBiologicalAutoAssignments } from "../../services/biologicalAutoAssignment";
import { Loader } from "../../../../shared/components/Loader";
import WarningConfirmationPopover from "../../../../shared/components/WarningConfirmationPopover";
import { PLOT_CARD_IDS } from "../../../../shared/layout/PlotCard/constants";
import useSaveBiologicalAssignments from "../../hooks/useSaveBiologicalAssignments";
import useSeedTranslations from "../../hooks/useSeedTranslations";
import AutoProdAssignment from "../AutoProdAssignment";
import { ErrorAutoScreen } from "../ErrorAutoScreen";
import ResetBtn from "../ResetBtn";
import StepNavigation from "../StepNavigation";
import GroupWiseTable, { type GroupWiseTableGroup } from "../GroupWiseTable";
import ApplicationProgress from "../ApplicationProgress";
import ApplicationSummary, {
  type ApplicationSummaryColumn,
  type ApplicationSummaryGroup,
} from "../ApplicationSummary";
import TreatmentSummarySection, {
  type TreatmentSummarySectionTreatment,
} from "./TreatmentSummarySection";
import TreatmentPlotSelection from "./TreatmentPlotSelection";
import { type GroupTableRow } from "../GroupTable";
import { usePlotSelection } from "../../context/PlotSelectionContext";
import {
  normalizeCpAssignments,
  type CpAssignment,
  type NormalizedCpApplication,
  type CpSavedTrialPlot,
} from "./normalizeCpAssignments";
import "./style.less";

interface BiologicalTreatment {
  products?: Array<{
    id: string;
    variety: string;
    rate: number;
    measure_unit: string;
  }> | null;
  tank_mix_rate?: { min?: number; max?: number };
  instructions?: string;
}

interface BiologicalApplication {
  description?: string;
}

export interface CPBiologicalProps
  extends Partial<
    Pick<ReturnType<typeof useNewTrialState>, "newTrialState" | "trialInfo">
  > {
  trialProtocolData?: {
    product_details?: {
      instructions?: string;
      biologicals?: {
        applications?: BiologicalApplication[];
        treatments?: BiologicalTreatment[] | BiologicalTreatment[][];
      };
    };
  } | null;
  hasSeeds?: boolean;
  hasNutrients?: boolean;
  onContinue?: () => void;
  onNavigateToSeeds?: () => void;
  onNavigateToPlotDesign?: () => void;
  onPlotSelectionActive?: (active: boolean) => void;
  onApplicationChange?: (applicationId: number) => void;
  onValidationChange?: (valid: boolean) => void;
  onCompletionChange?: (complete: boolean) => void;
  active?: boolean;
  onRegisterBeforeLeave?: (handler: (() => Promise<boolean>) | null) => void;
  onSavingChange?: (saving: boolean) => void;
  onRegisterReset?: (handler: (() => Promise<boolean>) | null) => void;
  onResetAvailabilityChange?: (available: boolean) => void;
}

const toOrdinalLabel = (value: number): string => {
  const lastTwoDigits = value % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${value}th`;
  }

  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
};

const UNTREATED_LABEL = "Untreated";

// The accent bar is coloured by treatment identity (like PlotsComponent's
// per-treatment colour bars), not by row position: every plot on the same
// treatment gets the same colour, and untreated plots render neutral.
const withAccentColorKeys = (
  groups: GroupWiseTableGroup[]
): GroupWiseTableGroup[] =>
  groups.map((group) => ({
    ...group,
    rows: group.rows.map((row) => ({
      ...row,
      accentColorKey:
        row.productLabel === UNTREATED_LABEL ? null : row.productLabel,
    })),
  }));

const toApplicationSummaryGroups = (
  groups: GroupWiseTableGroup[],
  application?: NormalizedCpApplication
): ApplicationSummaryGroup[] =>
  groups.map((group) => ({
    id: group.id,
    label: group.label,
    rows: group.rows.map((row) => {
      const assignment = application?.assignments[row.id];

      return {
        id: row.id,
        plot: row.plot,
        treatment: row.productLabel,
        accentColor:
          assignment?.kind === "assigned"
            ? getTreatmentColor(application, assignment.treatmentId)
            : assignment?.kind === "untreated"
            ? "#A3A9B9"
            : undefined,
      };
    }),
  }));

const toApplicationNumber = (value: string | number): number => Number(value);

const isTankVolumeValid = (
  value: number | null,
  min: number | undefined,
  max: number | undefined
): boolean =>
  value != null &&
  (min == null || value >= min) &&
  (max == null || value <= max);

// Flattens the shared plot/treatment dataset into the flat rows the
// per-treatment "Select plots" list needs, stripping the "Group " prefix
// GroupWiseTable's collapsible sections use since this list has no headers.
const getPlotRowsForProduct = (
  groups: GroupWiseTableGroup[],
  productLabel: string
): GroupTableRow[] =>
  groups.flatMap((group) =>
    group.rows
      .filter((row) => row.productLabel === productLabel)
      .map((row) => ({
        id: row.id,
        group: group.label.replace(/^Group /, ""),
        plot: row.plot,
      }))
  );

const getPlotRowsForIds = (
  groups: GroupWiseTableGroup[],
  selectedIds: string[]
): GroupTableRow[] => {
  const selected = new Set(selectedIds);
  return groups.flatMap((group) =>
    group.rows
      .filter((row) => selected.has(row.id))
      .map((row) => ({
        id: row.id,
        group: group.label.replace(/^Group /, ""),
        plot: row.plot,
      }))
  );
};

const getSavedTreatmentState = (
  applications: NormalizedCpApplication[]
): Record<number, Record<string, boolean>> =>
  Object.fromEntries(
    applications.map((application) => {
      const savedTreatments = new Set(
        Object.values(application.assignments)
          .filter(
            (
              assignment
            ): assignment is Extract<CpAssignment, { kind: "assigned" }> =>
              assignment.kind === "assigned"
          )
          .map((assignment) => `treatment-${assignment.treatmentId}`)
      );

      return [
        application.index + 1,
        Object.fromEntries([...savedTreatments].map((id) => [id, true])),
      ];
    })
  );

const getSavedTreatmentColor = (
  assignment: Extract<CpAssignment, { kind: "assigned" }>,
  fallback: string
): string => {
  if (typeof assignment.raw !== "object" || assignment.raw === null) {
    return fallback;
  }

  const color = (assignment.raw as { color?: unknown }).color;
  return typeof color === "string" && color.length > 0 ? color : fallback;
};

const getTreatmentColor = (
  application: NormalizedCpApplication | undefined,
  treatmentId: number
): string => {
  const fallback = getCircleColorBiologicals({ treated: true }, treatmentId);
  const assignment = Object.values(application?.assignments ?? {}).find(
    (entry) => entry.kind === "assigned" && entry.treatmentId === treatmentId
  );
  return assignment?.kind === "assigned"
    ? getSavedTreatmentColor(assignment, fallback)
    : fallback;
};

const CPBiological: FC<CPBiologicalProps> = ({
  newTrialState,
  trialInfo,
  trialProtocolData,
  hasSeeds = true,
  hasNutrients = true,
  onContinue,
  onNavigateToSeeds,
  onNavigateToPlotDesign,
  onPlotSelectionActive,
  onApplicationChange,
  onValidationChange,
  onCompletionChange,
  active = true,
  onRegisterBeforeLeave,
  onSavingChange,
  onRegisterReset,
  onResetAvailabilityChange,
}) => {
  const { t } = useTranslation();
  const seedText = useSeedTranslations();
  const { session, startSession, endSession, setAssignedTreatmentPlots } =
    usePlotSelection();

  const trialPlots = useMemo(() => {
    const design = newTrialState?.trial_plots ?? [];
    const saved = trialInfo?.trial_plots as typeof design | undefined;
    const source = design.length > 0 ? design : saved ?? [];
    return source.map((entry, index) => ({
      ...entry,
      plot: {
        ...entry.plot,
        ...saved?.[index]?.plot,
        id:
          design[index]?.plot?.id ??
          Number(`${PLOT_CARD_IDS.TRIAL_PLOTS_BASE}${index + 1}`),
      },
      properties:
        saved?.[index]?.properties ?? (entry as CpSavedTrialPlot).properties,
      replicants: entry.replicants ?? [],
    }));
  }, [newTrialState?.trial_plots, trialInfo?.trial_plots]);
  const isStripes =
    resolveSeedFlow({
      ...newTrialState,
      trial_plots: trialPlots,
    }) === "stripes";
  const designGroups = buildNutrientGroups(trialPlots, {
    ...newTrialState?.stripsData,
    isStripMode: isStripes,
  });
  const totalPlots = designGroups.reduce(
    (total, group) => total + group.plots.length,
    0
  );
  const totalLabel = isStripes
    ? formatTranslation(t("newRatesAndDosage.plotSummary.totalStrips"))
    : undefined;
  const plotColumnLabel = formatTranslation(
    t(
      isStripes
        ? "newRatesAndDosage.nutrient.stripeColumn"
        : "newRatesAndDosage.groupWiseTable.plot"
    )
  );
  const biologicals = trialProtocolData?.product_details?.biologicals;
  const normalizedCpAssignments = useMemo(
    () =>
      normalizeCpAssignments({
        applications: biologicals?.applications,
        treatments: biologicals?.treatments,
        trialPlots,
        biologicalAssignments:
          trialInfo?.plot_property_assignments
            ?.biological_treatment_assignments,
        isStripMode: isStripes,
      }),
    [
      biologicals?.applications,
      biologicals?.treatments,
      trialPlots,
      trialInfo?.plot_property_assignments?.biological_treatment_assignments,
      isStripes,
    ]
  );
  const [committedApplications, setCommittedApplications] = useState(
    normalizedCpAssignments.applications
  );
  const hasAssignmentDraft = useRef(false);
  const requestController = useRef<AbortController | null>(null);
  const navigationSaving = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);

  const treatments: TreatmentSummarySectionTreatment[] =
    normalizedCpAssignments.treatments.map(({ treatmentId, definition }) => {
      const treatment =
        typeof definition === "object" && definition !== null
          ? (definition as BiologicalTreatment)
          : {};

      return {
        id: `treatment-${treatmentId}`,
        title: formatTranslation(
          t("newTrial.treatmentBio.treatmentLabel", { index: treatmentId })
        ),
        products: (treatment.products ?? []).map((product) => ({
          id: product.id,
          name: product.variety,
          rate: `${product.rate} ${getProductUnit(product.measure_unit)}`,
        })),
        tankVolume:
          trialInfo?.standard_field_properties?.standard_tank_mix_rates?.[
            treatmentId - 1
          ] ??
          treatment.tank_mix_rate?.min ??
          null,
        tankVolumeMin: treatment.tank_mix_rate?.min,
        tankVolumeMax: treatment.tank_mix_rate?.max,
        instructions:
          trialProtocolData?.product_details?.instructions ??
          treatment.instructions ??
          "",
      };
    });
  const totalApplications = Math.max(
    1,
    normalizedCpAssignments.applications.length
  );
  const [autoAssignmentReviewRequired, setAutoAssignmentReviewRequired] =
    useState(false);
  const [reviewedApplications, setReviewedApplications] = useState<Set<number>>(
    () => new Set()
  );
  const [isAutoAssignView, setIsAutoAssignView] = useState(false);
  const [autoAssignmentFailed, setAutoAssignmentFailed] = useState(false);
  const [overwriteConfirmationOpen, setOverwriteConfirmationOpen] =
    useState(false);
  const [selectingTreatmentId, setSelectingTreatmentId] = useState<
    string | null
  >(null);
  const previousTrialId = useRef(trialInfo?.id);
  const [applicationValue, setApplicationValue] = useState<string | number>(1);
  const currentApplication = toApplicationNumber(applicationValue);
  const [summaryVisible, setSummaryVisible] = useState(false);
  const cpBiologicalRef = useRef<HTMLDivElement>(null);
  const applicationSummaryRef = useRef<HTMLDivElement>(null);
  const [completedApplications, setCompletedApplications] = useState<
    Record<number, GroupWiseTableGroup[]>
  >({});
  const [savedTreatments, setSavedTreatments] = useState<
    Record<number, Record<string, boolean>>
  >(() => getSavedTreatmentState(normalizedCpAssignments.applications));
  const [savingTreatmentId, setSavingTreatmentId] = useState<string | null>(
    null
  );
  const saveBiologicalAssignments = useSaveBiologicalAssignments();
  const [editedTankVolumes, setTankVolumes] = useState<
    Record<string, number | null>
  >({});
  const tankVolumes: Record<string, number | null> = Object.fromEntries(
    treatments.map((treatment) => [
      treatment.id,
      treatment.id in editedTankVolumes
        ? editedTankVolumes[treatment.id]
        : treatment.tankVolume,
    ])
  );
  useEffect(() => {
    if (hasAssignmentDraft.current && previousTrialId.current === trialInfo?.id)
      return;
    setCommittedApplications(normalizedCpAssignments.applications);
    setSavedTreatments(
      getSavedTreatmentState(normalizedCpAssignments.applications)
    );
  }, [normalizedCpAssignments, trialInfo?.id]);

  useEffect(() => {
    setIsAutoAssigning(false);
    setAutoAssignmentReviewRequired(false);
    setReviewedApplications(new Set());
    setApplicationValue(1);
    setIsAutoAssignView(false);
    setAutoAssignmentFailed(false);
    setOverwriteConfirmationOpen(false);
    return () => {
      requestController.current?.abort();
      requestController.current = null;
      hasAssignmentDraft.current = false;
    };
  }, [trialInfo?.id]);

  useEffect(() => {
    if (!active) setOverwriteConfirmationOpen(false);
  }, [active]);

  const busy = isSaving || isAutoAssigning || savingTreatmentId !== null;
  const selectionActive = selectingTreatmentId !== null;
  useEffect(() => {
    onSavingChange?.(busy);
  }, [busy, onSavingChange]);

  const selectedTreatmentSession =
    session?.tab === "biologicals" &&
    session.applicationId === currentApplication &&
    session.targetMode === (isStripes ? "stripe" : "plot")
      ? session
      : null;

  useEffect(() => {
    setAssignedTreatmentPlots(
      committedApplications.flatMap((application) =>
        Object.entries(application.assignments).flatMap(
          ([cellId, assignment]) =>
            assignment.kind === "assigned"
              ? [
                  {
                    cellId,
                    ownerId: `treatment-${assignment.treatmentId}`,
                    ownerLabel: formatTranslation(
                      t("newTrial.treatmentBio.treatmentLabel", {
                        index: assignment.treatmentId,
                      })
                    ),
                    applicationId: application.index + 1,
                    targetMode: isStripes ? "stripe" : "plot",
                    color: getSavedTreatmentColor(
                      assignment,
                      getCircleColorBiologicals(
                        { treated: true },
                        assignment.treatmentId
                      )
                    ),
                  },
                ]
              : []
        )
      )
    );
  }, [committedApplications, isStripes, setAssignedTreatmentPlots, t]);

  const handleTankVolumeChange = (
    treatmentId: string,
    value: number | null
  ): void => {
    hasAssignmentDraft.current = true;
    setTankVolumes((prev) => ({ ...prev, [treatmentId]: value }));
  };

  const handlePlotTreatmentChange = (rowId: string, value: string): void => {
    const treatmentIndex = treatments.findIndex(
      (entry) => entry.title === value
    );
    if (value !== UNTREATED_LABEL && treatmentIndex < 0) {
      console.error("Unknown biological treatment:", value);
      toast.error(formatTranslation(t("common.error.update")));
      return;
    }
    hasAssignmentDraft.current = true;
    setReviewedApplications((previous) => {
      if (!previous.has(currentApplication)) return previous;
      const next = new Set(previous);
      next.delete(currentApplication);
      return next;
    });
    setCommittedApplications((previous) =>
      previous.map((application) =>
        application.index !== currentApplication - 1
          ? application
          : {
              ...application,
              assignments: {
                ...application.assignments,
                [rowId]:
                  value === UNTREATED_LABEL
                    ? {
                        kind: "untreated",
                        raw: { treated: false, selected_treatment: [] },
                      }
                    : {
                        kind: "assigned",
                        treatmentId: treatmentIndex + 1,
                        raw: {
                          treated: true,
                          selected_treatment: [treatmentIndex + 1],
                          color: getTreatmentColor(
                            application,
                            treatmentIndex + 1
                          ),
                        },
                      },
              },
            }
      )
    );
    setCompletedApplications({});
  };

  const currentCommittedApplication = committedApplications.find(
    ({ index }) => index === currentApplication - 1
  );
  const treatmentTitlesById = new Map(
    treatments.map((treatment, index) => [index + 1, treatment.title])
  );
  const getApplicationGroups = (
    application: (typeof committedApplications)[number] | undefined
  ): GroupWiseTableGroup[] =>
    designGroups.map((group) => ({
      id: group.id,
      label:
        group.groupIndex === undefined
          ? formatTranslation(t("newRatesAndDosage.nutrient.stripes"))
          : `Group ${groupLetter(group.groupIndex)}`,
      rows: group.plots.map((plot) => {
        const assignment = application?.assignments[plot.id];
        return {
          id: plot.id,
          plot: plot.label,
          productLabel:
            assignment?.kind === "assigned"
              ? treatmentTitlesById.get(assignment.treatmentId) ?? ""
              : assignment?.kind === "untreated"
              ? UNTREATED_LABEL
              : "",
        };
      }),
    }));
  const plotGroups: GroupWiseTableGroup[] = designGroups.map((group) => ({
    id: group.id,
    label:
      group.groupIndex === undefined
        ? formatTranslation(t("newRatesAndDosage.nutrient.stripes"))
        : `Group ${groupLetter(group.groupIndex)}`,
    rows: group.plots.map((plot) => {
      const assignment: CpAssignment | undefined =
        currentCommittedApplication?.assignments[plot.id];
      const committedLabel =
        assignment?.kind === "assigned"
          ? treatmentTitlesById.get(assignment.treatmentId) ?? ""
          : assignment?.kind === "untreated"
          ? UNTREATED_LABEL
          : "";

      return {
        id: plot.id,
        plot: plot.label,
        productLabel: committedLabel,
        accentColor:
          assignment?.kind === "assigned"
            ? getTreatmentColor(
                currentCommittedApplication,
                assignment.treatmentId
              )
            : undefined,
        productOptions: [
          ...treatments.map((treatment) => treatment.title),
          UNTREATED_LABEL,
        ],
      };
    }),
  }));
  const targetIds = designGroups.flatMap((group) =>
    group.plots.map((plot) => plot.id)
  );
  const completedApplicationCount = committedApplications.filter(
    ({ assignments }) =>
      targetIds.length > 0 &&
      targetIds.every((targetId) => {
        const assignment = assignments[targetId];
        return (
          assignment?.kind === "assigned" || assignment?.kind === "untreated"
        );
      })
  ).length;
  const committedAssignedCount = plotGroups.reduce(
    (sum, group) =>
      sum +
      group.rows.filter(
        (row) => row.productLabel !== "" && row.productLabel !== UNTREATED_LABEL
      ).length,
    0
  );

  const hasValidTankVolume = (
    treatment: TreatmentSummarySectionTreatment
  ): boolean =>
    isTankVolumeValid(
      tankVolumes[treatment.id],
      treatment.tankVolumeMin,
      treatment.tankVolumeMax
    );
  const allTankVolumesValid =
    treatments.length > 0 && treatments.every(hasValidTankVolume);
  const autoAssignmentDisabled =
    selectionActive || busy || !allTankVolumesValid || !trialInfo?.id;
  const hasAssignedTargets = committedApplications.some((application) =>
    Object.values(application.assignments).some(
      (assignment) => assignment.kind === "assigned"
    )
  );
  const isTreatmentSaved = (
    application: NormalizedCpApplication | undefined,
    treatment: TreatmentSummarySectionTreatment
  ): boolean =>
    application !== undefined &&
    (savedTreatments[application.index + 1]?.[treatment.id] ??
      Object.values(application.assignments).some(
        (assignment) =>
          assignment.kind === "assigned" &&
          `treatment-${assignment.treatmentId}` === treatment.id
      ));
  const currentApplicationHasTreated = committedAssignedCount > 0;
  const everyApplicationHasTreated = committedApplications.every(
    (application) =>
      Object.values(application.assignments).some(
        (assignment) => assignment.kind === "assigned"
      )
  );
  const applicationOptions = Array.from(
    { length: totalApplications },
    (_, index) => ({
      label: toOrdinalLabel(index + 1),
      value: index + 1,
      disabled:
        index + 1 !== currentApplication &&
        (busy ||
          !currentApplicationHasTreated ||
          (autoAssignmentReviewRequired && index + 1 > currentApplication + 1)),
    })
  );
  const biologicalsComplete =
    allTankVolumesValid &&
    selectingTreatmentId == null &&
    completedApplicationCount === totalApplications;
  const allApplicationsReviewed =
    !autoAssignmentReviewRequired ||
    (currentApplication === totalApplications &&
      Array.from(
        { length: totalApplications - 1 },
        (_, index) => index + 1
      ).every((application) => reviewedApplications.has(application)));
  const canContinue =
    biologicalsComplete &&
    allApplicationsReviewed &&
    everyApplicationHasTreated;
  const tabComplete =
    completedApplicationCount === totalApplications && allApplicationsReviewed;
  const currentApplicationComplete =
    targetIds.length > 0 &&
    targetIds.every((id) => {
      const assignment = currentCommittedApplication?.assignments[id];
      return (
        assignment?.kind === "assigned" || assignment?.kind === "untreated"
      );
    });

  useEffect(() => {
    onValidationChange?.(canContinue);
  }, [canContinue, onValidationChange]);

  useEffect(() => {
    onCompletionChange?.(tabComplete);
  }, [onCompletionChange, tabComplete]);

  const handleAutoAssign = async (): Promise<void> => {
    if (busy || requestController.current) return;
    const controller = new AbortController();
    requestController.current = controller;
    setIsAutoAssigning(true);
    try {
      const response = await fetchBiologicalAutoAssignments(
        trialInfo?.id ?? "",
        controller.signal
      );
      if (controller.signal.aborted) return;
      const responsePlots: CpSavedTrialPlot[] =
        isStripes && response.trial_plots
          ? mergeStripPlotsIntoGroup(response.trial_plots, true)
          : response.trial_plots ?? [];
      if (response.trial_plots && responsePlots.length !== trialPlots.length) {
        throw new Error(
          "The automatic assignment plot design does not match this trial."
        );
      }
      const normalized = normalizeCpAssignments({
        applications: biologicals?.applications,
        treatments: biologicals?.treatments,
        trialPlots: trialPlots.map((entry, index) => ({
          ...entry,
          properties: responsePlots[index]?.properties,
        })),
        biologicalAssignments:
          response.plot_property_assignments?.biological_treatment_assignments,
        isStripMode: isStripes,
      });
      if (
        normalized.blockers.length > 0 ||
        targetIds.length === 0 ||
        normalized.applications.length === 0 ||
        normalized.applications.some((application) =>
          targetIds.some(
            (id) =>
              application.assignments[id]?.kind !== "assigned" &&
              application.assignments[id]?.kind !== "untreated"
          )
        )
      ) {
        throw new Error(
          "The automatic assignment API returned incomplete biological assignments."
        );
      }
      hasAssignmentDraft.current = true;
      setCommittedApplications(normalized.applications);
      setSavedTreatments(getSavedTreatmentState(normalized.applications));
      setCompletedApplications({});
      setAutoAssignmentReviewRequired(true);
      setReviewedApplications(new Set());
      setApplicationValue(1);
      setSummaryVisible(false);
      onApplicationChange?.(1);
      handleCancelSelectPlots();
      setAutoAssignmentFailed(false);
      setIsAutoAssignView(true);
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error("Failed to auto-assign biological treatments:", error);
        setAutoAssignmentFailed(true);
      }
    } finally {
      if (requestController.current === controller) {
        requestController.current = null;
        setIsAutoAssigning(false);
      }
    }
  };

  const saveApplications = async (
    applications: NormalizedCpApplication[],
    includeStandardFieldProperties = false,
    throughApplicationIndex?: number
  ): Promise<void> => {
    if (!trialInfo?.id) throw new Error("A saved trial is required.");
    const payload = buildBiologicalAssignmentsSnapshot({
      designGroups,
      trialPlots,
      applications,
      treatments: normalizedCpAssignments.treatments,
      throughApplicationIndex,
    });
    await saveBiologicalAssignments.mutateAsync({
      trialId: trialInfo.id,
      payload,
      ...(includeStandardFieldProperties
        ? {
            standardFieldProperties: {
              ...trialInfo.standard_field_properties,
              standard_tank_mix_rates: treatments.map(
                (entry) => tankVolumes[entry.id]
              ),
            },
          }
        : {}),
    });
  };

  const saveThroughApplication = async (
    applicationIndex: number,
    includeStandardFieldProperties = false
  ): Promise<void> => {
    if (
      !committedApplications.some((entry) => entry.index === applicationIndex)
    ) {
      throw new Error(
        `Biological application ${applicationIndex + 1} is missing.`
      );
    }

    await saveApplications(
      committedApplications,
      includeStandardFieldProperties,
      applicationIndex
    );
  };

  const saveBeforeNavigation = async (): Promise<boolean> => {
    if (navigationSaving.current || busy) return false;
    if (!canContinue) return false;
    if (autoAssignmentReviewRequired && !hasAssignmentDraft.current)
      return true;
    navigationSaving.current = true;
    setIsSaving(true);
    try {
      await saveApplications(committedApplications, true);
      hasAssignmentDraft.current = false;
      return true;
    } catch (error) {
      console.error(
        "Failed to save biological assignments before navigation:",
        error
      );
      toast.error(formatTranslation(t("common.error.update")));
      return false;
    } finally {
      navigationSaving.current = false;
      setIsSaving(false);
    }
  };

  const beforeLeaveRef = useRef(saveBeforeNavigation);
  beforeLeaveRef.current = saveBeforeNavigation;
  useEffect(() => {
    onRegisterBeforeLeave?.(async () => await beforeLeaveRef.current());
    return () => onRegisterBeforeLeave?.(null);
  }, [onRegisterBeforeLeave]);

  const handleContinue = async (): Promise<void> => {
    if (await saveBeforeNavigation()) onContinue?.();
  };

  const handleCancelSelectPlots = useCallback((): void => {
    if (session?.tab === "biologicals") {
      endSession({
        tab: "biologicals",
        ownerId: session.ownerId,
        applicationId: session.applicationId,
        targetMode: session.targetMode,
      });
    }
    setSelectingTreatmentId(null);
    onPlotSelectionActive?.(false);
  }, [endSession, onPlotSelectionActive, session]);

  const resetAvailable = committedApplications.some((application) =>
    Object.values(application.assignments).some(
      (assignment) => assignment.kind !== "missing"
    )
  );
  const resetInFlight = useRef(false);
  const handleReset = async (treatmentId?: string): Promise<boolean> => {
    if (busy || resetInFlight.current || !trialInfo?.id || !resetAvailable)
      return false;
    resetInFlight.current = true;
    setIsSaving(true);
    try {
      const reset = buildBiologicalReset({
        designGroups,
        trialPlots,
        applications: committedApplications,
        treatments: normalizedCpAssignments.treatments,
        ...(treatmentId
          ? {
              target: {
                applicationIndex: currentApplication - 1,
                treatmentIndex: Number(treatmentId.replace("treatment-", "")),
              },
            }
          : {}),
      });
      await saveBiologicalAssignments.mutateAsync({
        trialId: trialInfo.id,
        payload: reset.payload,
      });
      hasAssignmentDraft.current = true;
      setCommittedApplications(reset.applications);
      setSavedTreatments(getSavedTreatmentState(reset.applications));
      setCompletedApplications({});
      setReviewedApplications(new Set());
      handleCancelSelectPlots();
      if (!treatmentId) {
        setAutoAssignmentReviewRequired(false);
        setIsAutoAssignView(false);
        setApplicationValue(1);
        onApplicationChange?.(1);
      }
      return true;
    } catch (error) {
      console.error("Failed to reset biological assignments:", error);
      toast.error(formatTranslation(t("common.error.update")));
      return false;
    } finally {
      resetInFlight.current = false;
      setIsSaving(false);
    }
  };
  const resetRef = useRef(handleReset);
  resetRef.current = handleReset;
  useEffect(() => {
    onRegisterReset?.(async () => await resetRef.current());
    return () => onRegisterReset?.(null);
  }, [onRegisterReset]);
  useEffect(() => {
    onResetAvailabilityChange?.(resetAvailable);
  }, [onResetAvailabilityChange, resetAvailable]);

  useEffect(() => {
    if (!active && selectingTreatmentId != null) handleCancelSelectPlots();
  }, [active, handleCancelSelectPlots, selectingTreatmentId]);

  useEffect(() => {
    if (previousTrialId.current !== trialInfo?.id) {
      previousTrialId.current = trialInfo?.id;
      if (selectingTreatmentId == null) return;
      handleCancelSelectPlots();
    }
  }, [handleCancelSelectPlots, selectingTreatmentId, trialInfo?.id]);

  const handleSelectPlots = (treatmentId: string): void => {
    if (selectionActive || busy) return;
    setSummaryVisible(false);
    const treatmentNumber = Number(treatmentId.replace("treatment-", ""));
    const application = committedApplications.find(
      ({ index }) => index === currentApplication - 1
    );
    const selectedIds = Object.entries(application?.assignments ?? {})
      .filter(
        ([, assignment]) =>
          assignment.kind === "assigned" &&
          assignment.treatmentId === treatmentNumber
      )
      .map(([targetId]) => targetId);
    const disabledIds = Object.entries(application?.assignments ?? {})
      .filter(([, assignment]) => assignment.kind === "blocked")
      .map(([targetId]) => targetId);
    startSession({
      tab: "biologicals",
      ownerId: treatmentId,
      applicationId: currentApplication,
      targetMode: isStripes ? "stripe" : "plot",
      targetIds,
      selectedIds,
      disabledIds,
      color: getTreatmentColor(application, treatmentNumber),
    });
    setSelectingTreatmentId(treatmentId);
    onPlotSelectionActive?.(true);
  };

  const handleSaveSelectPlots = async (treatmentId: string): Promise<void> => {
    if (!trialInfo?.id || savingTreatmentId !== null) return;

    const treatmentNumber = Number(treatmentId.replace("treatment-", ""));
    const selectedIds =
      session?.tab === "biologicals" &&
      session.ownerId === treatmentId &&
      session.applicationId === currentApplication &&
      session.targetMode === (isStripes ? "stripe" : "plot")
        ? session.selectedIds
        : [];
    const selected = new Set(selectedIds);
    const reassigned = new Set(
      session?.tab === "biologicals" &&
      session.ownerId === treatmentId &&
      session.applicationId === currentApplication &&
      session.targetMode === (isStripes ? "stripe" : "plot")
        ? session.reassignedIds
        : []
    );
    const applicationIndex = currentApplication - 1;
    const color = getTreatmentColor(
      committedApplications[applicationIndex],
      treatmentNumber
    );
    const nextAssignments = {
      ...(committedApplications[applicationIndex]?.assignments ?? {}),
    };
    targetIds.forEach((targetId) => {
      const current = nextAssignments[targetId];
      if (current?.kind === "blocked") return;
      if (
        current?.kind === "assigned" &&
        current.treatmentId !== treatmentNumber &&
        !reassigned.has(targetId)
      ) {
        return;
      }
      nextAssignments[targetId] = selected.has(targetId)
        ? {
            kind: "assigned",
            treatmentId: treatmentNumber,
            raw: {
              treated: true,
              selected_treatment: [treatmentNumber],
              color,
            },
          }
        : current?.kind === "assigned"
        ? { kind: "missing" }
        : current ?? { kind: "missing" };
    });
    const nextSavedTreatments = {
      ...savedTreatments[currentApplication],
      [treatmentId]: true,
    };
    Object.entries(nextAssignments).forEach(([cellId, assignment]) => {
      if (assignment.kind === "missing") {
        nextAssignments[cellId] = {
          kind: "untreated",
          raw: { treated: false, selected_treatment: [] },
        };
      }
    });

    const nextApplications = committedApplications.map((application) =>
      application.index === applicationIndex
        ? { ...application, assignments: nextAssignments }
        : application
    );
    const untreatedCellIds = Object.entries(nextAssignments)
      .filter(([, assignment]) => assignment.kind === "untreated")
      .map(([cellId]) => cellId);

    setSavingTreatmentId(treatmentId);
    try {
      const payload = buildBiologicalTreatmentAssignmentsPayload({
        designGroups,
        trialPlots,
        applications: committedApplications,
        treatments: normalizedCpAssignments.treatments,
        applicationIndex,
        treatmentIndex: treatmentNumber,
        selectedCellIds: selectedIds,
        untreatedCellIds,
        color,
      });
      await saveBiologicalAssignments.mutateAsync({
        trialId: String(trialInfo.id),
        payload,
      });

      setCommittedApplications(nextApplications);
      setSavedTreatments((previous) => ({
        ...previous,
        [currentApplication]: nextSavedTreatments,
      }));
      setCompletedApplications((previous) => ({
        ...previous,
        [currentApplication]: getApplicationGroups(
          nextApplications[applicationIndex]
        ),
      }));
      handleCancelSelectPlots();
    } catch (error) {
      console.error("Failed to save biological treatment assignments:", error);
      toast.error(formatTranslation(t("common.error.update")));
    } finally {
      setSavingTreatmentId(null);
    }
  };

  const handleApplicationChange = async (
    value: string | number
  ): Promise<void> => {
    const nextApplication = toApplicationNumber(value);
    if (nextApplication === currentApplication) return;
    if (busy || !currentApplicationHasTreated) return;
    if (autoAssignmentReviewRequired && nextApplication > currentApplication) {
      if (
        nextApplication !== currentApplication + 1 ||
        busy ||
        navigationSaving.current ||
        !currentApplicationComplete
      ) {
        return;
      }

      navigationSaving.current = true;
      setIsSaving(true);
      try {
        await saveThroughApplication(currentApplication - 1);
        setReviewedApplications((previous) => {
          const next = new Set(previous);
          next.add(currentApplication);
          return next;
        });
        setCompletedApplications((previous) => ({
          ...previous,
          [currentApplication]: plotGroups,
        }));
      } catch (error) {
        console.error(
          "Failed to save biological applications before continuing:",
          error
        );
        toast.error(formatTranslation(t("common.error.update")));
        return;
      } finally {
        navigationSaving.current = false;
        setIsSaving(false);
      }
    }

    handleCancelSelectPlots();
    setSummaryVisible(false);
    setApplicationValue(nextApplication);
    onApplicationChange?.(nextApplication);
  };

  const handleNextApplication = async (): Promise<void> => {
    if (currentApplication >= totalApplications || navigationSaving.current) {
      return;
    }
    if (!autoAssignmentReviewRequired) {
      if (isAutoAssignView) {
        setCompletedApplications((previous) => ({
          ...previous,
          [currentApplication]: plotGroups,
        }));
      }
      await handleApplicationChange(currentApplication + 1);
      return;
    }
    await handleApplicationChange(currentApplication + 1);
  };

  const activeSelectedIds =
    selectingTreatmentId != null &&
    selectedTreatmentSession?.ownerId === selectingTreatmentId
      ? selectedTreatmentSession.selectedIds
      : [];
  const activeSelectedIdSet = new Set(activeSelectedIds);
  const activeTreatmentNumber = selectingTreatmentId
    ? Number(selectingTreatmentId.replace("treatment-", ""))
    : null;
  const activeTreatmentHasCommittedTargets =
    activeTreatmentNumber != null &&
    Object.values(currentCommittedApplication?.assignments ?? {}).some(
      (assignment) =>
        assignment.kind === "assigned" &&
        assignment.treatmentId === activeTreatmentNumber
    );
  const activeTreatmentTitle = treatments.find(
    ({ id }) => id === selectingTreatmentId
  )?.title;
  const effectivePlotGroups =
    selectingTreatmentId == null
      ? plotGroups
      : plotGroups.map((group) => ({
          ...group,
          rows: group.rows.map((row) => {
            const committed = currentCommittedApplication?.assignments[row.id];
            if (activeSelectedIdSet.has(row.id)) {
              return { ...row, productLabel: activeTreatmentTitle ?? "" };
            }
            if (
              activeTreatmentNumber != null &&
              committed?.kind === "assigned" &&
              committed.treatmentId === activeTreatmentNumber
            ) {
              return { ...row, productLabel: UNTREATED_LABEL };
            }
            return row;
          }),
        }));
  const selectedRows = getPlotRowsForIds(
    effectivePlotGroups,
    activeSelectedIds
  );
  const hasEffectiveTreatedPlots = effectivePlotGroups.some((group) =>
    group.rows.some(
      (row) => row.productLabel !== "" && row.productLabel !== UNTREATED_LABEL
    )
  );
  const untreatedRows = getPlotRowsForProduct(
    effectivePlotGroups.map((group) => ({
      ...group,
      rows: group.rows.map((row) =>
        hasEffectiveTreatedPlots &&
        row.productLabel === "" &&
        currentCommittedApplication?.assignments[row.id]?.kind === "missing"
          ? { ...row, productLabel: UNTREATED_LABEL }
          : row
      ),
    })),
    UNTREATED_LABEL
  );

  const previousApplication = currentApplication - 1;
  const previousCommittedApplication = committedApplications.find(
    ({ index }) => index === previousApplication - 1
  );
  const previousHasSavedAssignments = Object.values(
    previousCommittedApplication?.assignments ?? {}
  ).some(
    (assignment) =>
      assignment.kind === "assigned" || assignment.kind === "untreated"
  );
  const previousGroups =
    completedApplications[previousApplication] ??
    (previousHasSavedAssignments
      ? getApplicationGroups(previousCommittedApplication)
      : undefined);
  const previousOrdinal = toOrdinalLabel(previousApplication);
  const isMultipleApplicationSummary = totalApplications > 2;
  const previousApplicationColumns: ApplicationSummaryColumn[] =
    isMultipleApplicationSummary
      ? committedApplications.flatMap((application) => {
          const applicationNumber = application.index + 1;
          const hasSavedAssignments = Object.values(
            application.assignments
          ).some(
            (assignment) =>
              assignment.kind === "assigned" || assignment.kind === "untreated"
          );

          if (applicationNumber >= currentApplication || !hasSavedAssignments) {
            return [];
          }

          return [
            {
              id: `application-${applicationNumber}`,
              label: formatTranslation(
                t("newRatesAndDosage.cpBiological.applicationTitle", {
                  application: toOrdinalLabel(applicationNumber),
                })
              ),
              groups: toApplicationSummaryGroups(
                completedApplications[applicationNumber] ??
                  getApplicationGroups(application),
                application
              ),
            },
          ];
        })
      : [];
  const hasPreviousSummary = isMultipleApplicationSummary
    ? previousApplicationColumns.length > 0
    : previousGroups !== undefined;
  const summaryTitle = isMultipleApplicationSummary
    ? formatTranslation(
        t("newRatesAndDosage.cpBiological.previousApplicationsTitle")
      )
    : formatTranslation(
        t("newRatesAndDosage.cpBiological.applicationTitle", {
          application: previousOrdinal,
        })
      );
  const summaryContent = hasPreviousSummary && (
    <div ref={applicationSummaryRef}>
      <ApplicationSummary
        title={summaryTitle}
        groups={
          isMultipleApplicationSummary
            ? previousApplicationColumns[0]?.groups ?? []
            : toApplicationSummaryGroups(previousGroups ?? [])
        }
        applicationColumns={
          isMultipleApplicationSummary ? previousApplicationColumns : undefined
        }
        plotColumnLabel={plotColumnLabel}
        treatmentColumnLabel={formatTranslation(
          t("newRatesAndDosage.cpBiological.treatmentColumn")
        )}
        onClose={() => {
          setSummaryVisible(false);
        }}
      />
    </div>
  );

  useEffect(() => {
    if (!summaryVisible) return;

    const handleDocumentClick = (event: MouseEvent): void => {
      if (
        event.target instanceof Node &&
        !applicationSummaryRef.current?.contains(event.target) &&
        !(
          event.target instanceof Element &&
          cpBiologicalRef.current?.contains(event.target) &&
          event.target.closest(".rd-treatment-summary-section__summary-toggle")
        )
      ) {
        setSummaryVisible(false);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [summaryVisible]);

  const summarySection = (
    <fieldset
      className="rd-cp-biological__control-group"
      disabled={selectionActive}
    >
      <TreatmentSummarySection
        treatments={treatments}
        tankVolumes={tankVolumes}
        onTankVolumeChange={handleTankVolumeChange}
        applicationOptions={applicationOptions}
        applicationValue={applicationValue}
        onApplicationChange={(value) => {
          void handleApplicationChange(value);
        }}
        previousApplicationLabel={
          hasPreviousSummary
            ? formatTranslation(
                t(
                  isMultipleApplicationSummary
                    ? summaryVisible
                      ? "newRatesAndDosage.cpBiological.hidePreviousApplications"
                      : "newRatesAndDosage.cpBiological.showPreviousApplications"
                    : summaryVisible
                    ? "newRatesAndDosage.cpBiological.hideApplication"
                    : "newRatesAndDosage.cpBiological.showApplication",
                  { application: previousOrdinal }
                )
              )
            : undefined
        }
        summaryVisible={summaryVisible}
        summaryContent={summaryContent}
        summaryWidth={
          isMultipleApplicationSummary
            ? Math.max(244, 92 + 126 * previousApplicationColumns.length)
            : 280
        }
        onToggleSummary={() => {
          setSummaryVisible((previous) => !previous);
        }}
      />
    </fieldset>
  );

  if (autoAssignmentFailed) {
    return (
      <ErrorAutoScreen
        isRetrying={isAutoAssigning}
        onRetry={() => {
          void handleAutoAssign();
        }}
        onClose={() => {
          setAutoAssignmentFailed(false);
        }}
      />
    );
  }

  if (isAutoAssignView) {
    const treated = committedAssignedCount;

    return (
      <div
        ref={cpBiologicalRef}
        className="rd-cp-biological"
        data-testid="cp-biological-auto-assign"
      >
        {busy && <Loader mask message="" />}
        <div className="rd-cp-biological__auto-assign-header">
          <button
            type="button"
            className="rd-cp-biological__back-btn"
            aria-label={formatTranslation(
              t("newRatesAndDosage.autoProductAssignment.back")
            )}
            onClick={() => {
              setIsAutoAssignView(false);
            }}
          >
            <ArrowLeftOutlined aria-hidden />
          </button>
          <span className="rd-cp-biological__auto-assign-title">
            {formatTranslation(t("newRatesAndDosage.autoProdAssignment.label"))}
          </span>
        </div>

        {summarySection}

        <PlotSummary
          totalPlots={totalPlots}
          totalLabel={totalLabel}
          assigned={treated}
          applicationAssigned={completedApplicationCount}
          applicationTotal={totalApplications}
          assignedLabel={formatTranslation(
            t("newRatesAndDosage.plotSummary.treated")
          )}
          missingLabel={formatTranslation(
            t("newRatesAndDosage.plotSummary.untreated")
          )}
        />

        <GroupWiseTable
          groups={withAccentColorKeys(plotGroups)}
          onProductChange={handlePlotTreatmentChange}
          plotColumnLabel={plotColumnLabel}
          plotColumnUnit=""
          selectColumnLabel={formatTranslation(
            t("newRatesAndDosage.groupWiseTable.selectTreatment")
          )}
          selectColumnUnit=""
          showCheckbox={false}
          showSeedRate={false}
        />

        <ApplicationProgress
          current={currentApplication}
          total={totalApplications}
          nextLabel={formatTranslation(
            t("newRatesAndDosage.applicationProgress.nextApplication")
          )}
          onNext={() => {
            void handleNextApplication();
          }}
          nextDisabled={
            busy ||
            !currentApplicationHasTreated ||
            currentApplication >= totalApplications ||
            (autoAssignmentReviewRequired &&
              (busy || !currentApplicationComplete))
          }
        />

        <StepNavigation
          backLabel={formatTranslation(
            t("newRatesAndDosage.autoProductAssignment.back")
          )}
          nextLabel={formatTranslation(
            t(
              hasNutrients
                ? "newRatesAndDosage.tabs.nutrient"
                : "newRatesAndDosage.footer.summary"
            )
          )}
          onBack={() => {
            setIsAutoAssignView(false);
          }}
          onNext={() => {
            void handleContinue();
          }}
          nextDisabled={busy || !canContinue}
        />
      </div>
    );
  }

  return (
    <div
      ref={cpBiologicalRef}
      className="rd-cp-biological"
      data-testid="cp-biological"
    >
      {busy && <Loader mask message="" />}
      {summarySection}

      <PlotSummary
        totalPlots={totalPlots}
        totalLabel={totalLabel}
        assigned={committedAssignedCount}
        assignedLabel={formatTranslation(
          t("newRatesAndDosage.plotSummary.treated")
        )}
        missingLabel={formatTranslation(
          t("newRatesAndDosage.plotSummary.untreated")
        )}
        applicationAssigned={completedApplicationCount}
        applicationTotal={totalApplications}
      />

      <WarningConfirmationPopover
        open={overwriteConfirmationOpen}
        message={seedText("overwriteConfirmation")}
        confirmText={seedText("proceed")}
        cancelText={seedText("cancel")}
        confirmDisabled={autoAssignmentDisabled}
        onCancel={() => {
          setOverwriteConfirmationOpen(false);
        }}
        onConfirm={() => {
          setOverwriteConfirmationOpen(false);
          void handleAutoAssign();
        }}
      >
        <AutoProdAssignment
          disabled={autoAssignmentDisabled}
          onClick={() => {
            if (hasAssignedTargets) {
              setOverwriteConfirmationOpen(true);
              return;
            }
            void handleAutoAssign();
          }}
        />
      </WarningConfirmationPopover>

      <div className="rd-cp-biological__products">
        {treatments.map((treatment, treatmentIndex) => {
          const selectedCount = getPlotRowsForProduct(
            plotGroups,
            treatment.title
          ).length;
          const selectionLabel = seedText(
            isStripes ? "stripsSelected" : "plotsSelected",
            { count: selectedCount }
          );
          const selectionLabelParts = selectionLabel.split(
            String(selectedCount)
          );

          return selectingTreatmentId === treatment.id ? (
            <TreatmentPlotSelection
              key={treatment.id}
              title={treatment.title}
              // Select Plots is only reachable once hasValidTankVolume is
              // true, so the value is never null here.
              subtitle={formatTranslation(
                t("newRatesAndDosage.cpBiological.tankVolumeMeta", {
                  value: tankVolumes[treatment.id],
                })
              )}
              accentColorIndex={(treatmentIndex % 4) + 1}
              accentColor={getTreatmentColor(
                currentCommittedApplication,
                treatmentIndex + 1
              )}
              rows={selectedRows}
              isStripes={isStripes}
              plotColumnLabel={isStripes ? plotColumnLabel : undefined}
              onReset={() => {
                void handleReset(treatment.id);
              }}
              resetDisabled={busy || !activeTreatmentHasCommittedTargets}
              onCancel={handleCancelSelectPlots}
              onSave={() => {
                void handleSaveSelectPlots(treatment.id);
              }}
              saveDisabled={
                !trialInfo?.id ||
                savingTreatmentId !== null ||
                !hasValidTankVolume(treatment) ||
                (selectedRows.length === 0 &&
                  (!activeTreatmentHasCommittedTargets ||
                    !hasEffectiveTreatedPlots))
              }
            />
          ) : (
            <ProductCard
              key={treatment.id}
              className={
                selectedCount > 0
                  ? "rd-cp-biological__assigned-card"
                  : undefined
              }
              productName={treatment.title}
              indicatorColor={getTreatmentColor(
                currentCommittedApplication,
                treatmentIndex + 1
              )}
              variant={isStripes ? "stripes" : "plots"}
              isSaved={isTreatmentSaved(currentCommittedApplication, treatment)}
              headingAction={
                selectedCount > 0 ? (
                  <span className="rd-cp-biological__selected-count">
                    <span
                      className="rd-cp-biological__selection-separator"
                      aria-hidden="true"
                    >
                      &bull;
                    </span>
                    <span>
                      {selectionLabelParts.map((part, index) => (
                        <Fragment key={index}>
                          {index > 0 && <strong>{selectedCount}</strong>}
                          {part}
                        </Fragment>
                      ))}
                    </span>
                  </span>
                ) : undefined
              }
              headerAction={
                <ResetBtn
                  disabled={
                    selectionActive ||
                    busy ||
                    !Object.values(
                      currentCommittedApplication?.assignments ?? {}
                    ).some(
                      (assignment) =>
                        assignment.kind === "assigned" &&
                        assignment.treatmentId === treatmentIndex + 1
                    )
                  }
                  onClick={() => {
                    void handleReset(treatment.id);
                  }}
                />
              }
              protocolText={formatTranslation(
                t("newRatesAndDosage.cpBiological.tankVolumeMeta", {
                  value: tankVolumes[treatment.id] ?? "-",
                })
              )}
              selectPlotsDisabled={
                selectionActive || busy || !hasValidTankVolume(treatment)
              }
              onSelectPlots={() => {
                handleSelectPlots(treatment.id);
              }}
            />
          );
        })}
      </div>

      {(selectingTreatmentId != null || untreatedRows.length > 0) && (
        <fieldset
          className="rd-cp-biological__control-group"
          disabled={selectionActive}
        >
          <TreatmentPlotSelection
            title={formatTranslation(
              t("newRatesAndDosage.plotSummary.untreated")
            )}
            accentColorIndex={null}
            rows={untreatedRows}
            isStripes={isStripes}
            plotColumnLabel={formatTranslation(
              t(
                isStripes
                  ? "newRatesAndDosage.nutrient.stripeColumn"
                  : "newRatesAndDosage.groupTable.plots"
              )
            )}
            showActions={false}
          />
        </fieldset>
      )}

      <fieldset
        className="rd-cp-biological__control-group"
        disabled={selectionActive}
      >
        <ApplicationProgress
          current={currentApplication}
          total={totalApplications}
          nextLabel={formatTranslation(
            t("newRatesAndDosage.applicationProgress.nextApplication")
          )}
          onNext={() => {
            void handleNextApplication();
          }}
          nextDisabled={
            busy ||
            !currentApplicationHasTreated ||
            currentApplication >= totalApplications ||
            (autoAssignmentReviewRequired &&
              (busy || !currentApplicationComplete))
          }
        />

        <StepNavigation
          backLabel={formatTranslation(
            t(
              hasSeeds
                ? "newRatesAndDosage.tabs.seeds"
                : "newRatesAndDosage.footer.plotDesign"
            )
          )}
          nextLabel={formatTranslation(
            t(
              hasNutrients
                ? "newRatesAndDosage.tabs.nutrient"
                : "newRatesAndDosage.footer.summary"
            )
          )}
          onBack={hasSeeds ? onNavigateToSeeds : onNavigateToPlotDesign}
          onNext={() => {
            void handleContinue();
          }}
          nextDisabled={busy || !canContinue}
        />
      </fieldset>
    </div>
  );
};

export default CPBiological;
